/**
 * The enforcement console.
 *
 * These were five stacked panels down the right-hand side, and on a 1080-tall
 * screen the column ran off the bottom: the key list and part of the legend
 * were simply not on the display. Tabs fix that, but they are not only a fix.
 *
 * CALIBRATION.md finding 1: **alerts outnumber searches nearly twelve to one.**
 * The enforcement experience is an alert stream you triage, not a query console
 * you drive, and building it query-first inverts the real job. So alerts is the
 * tab that opens, and searching is a thing you deliberately switch to.
 *
 * On phones this becomes a sheet that slides up from the bottom, because the
 * map is the thing worth the screen and a console that permanently occupies a
 * third of a phone is a console nobody can drive around.
 *
 * ## It is closed until you open it, and that is the point
 *
 * DESIGN.md section 4 is explicit, and says the build had already got this
 * wrong by leaving it implicit:
 *
 * > **The world is the game.** The map and the terminal are devices inside that
 * > world, not the world. [...] **Opening one costs you the road.** That is the
 * > entire reason the split matters. While the terminal is up you are reading
 * > records instead of watching traffic, and traffic keeps moving.
 *
 * A console permanently on screen has no cost, and a query interface with no
 * cost makes the friction the whole enforcement design turns on evaporate.
 * So it starts closed, it opens over the world rather than beside it, and the
 * test §4 sets is that **a full session can be played without opening it** --
 * badly and blindly, but played.
 */

export type TabName =
  | 'work'
  | 'garage'
  | 'store'
  | 'flocked'
  | 'alerts'
  | 'query'
  | 'dispatch'
  | 'standing'
  | 'audit'
  | 'cameras'
  | 'scanner'
  | 'settings'
  | 'admin';

export class ConsolePanel {
  private readonly root: HTMLElement;
  private readonly tabs: HTMLElement;
  /** The phone's home screen, and the bar that sits above an app it opened. */
  private readonly home: HTMLElement;
  private readonly phoneBar: HTMLElement;
  private readonly phoneTitle: HTMLElement;
  /** Which app is up, or null for the home screen. Only meaningful on a phone. */
  private openApp: TabName | null = null;
  /** Whether this client holds the administrator's key. */
  private administrator = false;
  /** Whether the administrator's own area is up, in place of the two roles. */
  private adminView = false;
  private readonly bodies = new Map<TabName, HTMLElement>();
  private active: TabName = 'work';
  private shiftLeft = 0;
  private restLeft = 0;
  private showing = false;
  private enforcing = false;
  private readonly opener: HTMLButtonElement;
  private live = false;
  /** Tabs with something new in them since the player last looked. */
  private readonly unread = new Set<TabName>();

  private readonly onSide: (want: 'civilian' | 'enforcement') => void;
  /** Told which app was opened, so it can go and get whatever that app shows. */
  private onOpen: ((name: TabName) => void) | null = null;

  constructor(onSide: (want: 'civilian' | 'enforcement') => void) {
    this.onSide = onSide;
    this.root = must('console');
    this.root.hidden = true;
    this.opener = must('console-toggle') as HTMLButtonElement;
    this.opener.addEventListener('click', () => this.toggle());
    this.tabs = this.root.querySelector('.tabs') as HTMLElement;
    this.home = must('home');
    this.phoneBar = must('phone-bar');
    this.phoneTitle = must('phone-title');

    this.home.addEventListener('click', (event) => {
      const tile = (event.target as HTMLElement).closest<HTMLElement>('.app-tile');
      const name = tile?.dataset.tab as TabName | undefined;
      if (name) this.select(name);
    });
    // Out of an app and back to the apps, which is a thing a phone can do and
    // a row of tabs cannot.
    must('phone-back').addEventListener('click', () => this.goHome());

    for (const name of [
      'work',
      'garage',
      'store',
      'flocked',
      'alerts',
      'query',
      'dispatch',
      'standing',
      'audit',
      'cameras',
      'scanner',
      'settings',
      'admin',
    ] as TabName[]) {
      this.bodies.set(name, must(name));
    }

    // Everybody gets the console, because on a phone it is the only interface
    // there is: the desktop's left-hand panels have nowhere to go on a 414-wide
    // screen, and a civilian with no console would be left with a map and no
    // way to reach their own cars.
    this.setRole(false);

    const sides = must('sides');
    sides.addEventListener('click', (event) => {
      const want = (event.target as HTMLElement).dataset.side;
      if (want === 'admin') {
        this.enterAdmin();
      } else if (want === 'enforcement' || want === 'civilian') {
        const already = (want === 'enforcement') === this.enforcing;
        this.adminView = false;
        // Coming back from the admin area to the role already held does not
        // change sides on the server, so it would leave the admin panel up. Put
        // the role's own screens back directly in that case.
        if (already) this.setRole(this.enforcing);
        else this.onSide(want);
      }
    });

    this.tabs.addEventListener('click', (event) => {
      const name = (event.target as HTMLElement).dataset.tab as TabName | undefined;
      if (name) this.select(name);
    });
  }

  /**
   * Which tabs exist for this player.
   *
   * The enforcement tabs are not disabled for a civilian, they are not there,
   * because a greyed-out "search" tab would tell everybody that a search
   * interface exists and what it is called -- and the civilian side of this
   * game is supposed to learn what the network can do by being subject to it.
   */
  /**
   * How long is left, on either side of it.
   *
   * The button has to say what signing on commits you to, and what a refusal is
   * waiting for. A gate that will not say why is a bug wearing a rule's hat.
   */
  setShift(shiftEnds: number, restEnds: number, now: number, daySeconds: number): void {
    // Rounded rather than ceiled: the client's clock trails the server's by a
    // tick or two, so a four-hour shift measures a hair over four and ceiling
    // it announced five.
    const hours = (until: number): number =>
      Math.max(0, Math.round(((until - now) / daySeconds) * 24));
    this.shiftLeft = shiftEnds > now ? hours(shiftEnds) : 0;
    this.restLeft = restEnds > now ? hours(restEnds) : 0;
    this.paintSides();
  }

  /**
   * The administrator's own area.
   *
   * Its own side beside Civilian and Authority, not a tab wedged into the
   * authority strip. It shows only the oversight panel -- the resets and the
   * plate record -- because that is a different seat from the shift: an officer
   * searches the network, an administrator audits it and can empty the county.
   */
  private enterAdmin(): void {
    if (!this.administrator) return;
    this.adminView = true;
    this.root.classList.add('console-dashboard');
    this.active = 'admin';
    this.onOpen?.('admin');
    for (const [key, body] of this.bodies) body.hidden = key !== 'admin';
    this.tabs.hidden = true;
    this.home.hidden = true;
    this.phoneBar.hidden = true;
    this.hideAppActions();
    this.paintSideActive();
    this.paintOpener();
  }

  /** Which of the three sides reads as the one you are in. */
  private paintSideActive(): void {
    for (const button of this.root.querySelectorAll<HTMLElement>('.side')) {
      const side = button.dataset.side;
      const on = this.adminView
        ? side === 'admin'
        : side === (this.enforcing ? 'enforcement' : 'civilian');
      button.classList.toggle('active', on);
      button.setAttribute('aria-pressed', String(on));
    }
  }

  private paintSides(): void {
    for (const button of this.root.querySelectorAll<HTMLElement>('.side')) {
      if (button.dataset.side !== 'enforcement') continue;
      // The side is what it is called; the shift is a note on it. Naming the
      // button after the action ("sign on") hid what the two halves of the game
      // actually are.
      button.textContent =
        this.shiftLeft > 0
          ? `Authority · ${this.shiftLeft}h`
          : this.restLeft > 0
            ? `Authority · off duty ${this.restLeft}h`
            : 'Authority';
      button.classList.toggle('resting', this.restLeft > 0 && this.shiftLeft === 0);
    }
  }

  /** Say that this client is an administrator, which shows the Admin tab. */
  setAdministrator(is: boolean): void {
    this.administrator = is;
    const adminSide = document.getElementById('side-admin');
    if (adminSide) adminSide.hidden = !is;
    if (!is) this.adminView = false;
    this.setRole(this.enforcing);
  }

  setRole(enforcement: boolean): void {
    this.enforcing = enforcement;
    // A real role was chosen, so the administrator's own area is not up.
    this.adminView = false;
    this.root.classList.remove('console-dashboard');
    // Admin lives in its own side now, not a tab, so its screen is closed
    // whenever a role's screens are shown.
    this.bodies.get('admin')?.setAttribute('hidden', '');
    this.paintOpener();
    // One side at a time. A civilian never sees the console's tabs and an
    // officer never sees the work board, because the whole point is what each
    // half looks like from the inside.
    const civilianTabs = new Set<TabName>(['work', 'garage', 'flocked', 'cameras', 'scanner']);
    for (const button of this.tabs.querySelectorAll<HTMLElement>('.tab')) {
      const name = button.dataset.tab as TabName;
      // Settings is about what the map draws rather than about either half of
      // the game, so both sides get it.
      const allowed =
        name === 'settings'
          ? true
          : enforcement
            ? !civilianTabs.has(name)
            : civilianTabs.has(name);
      button.hidden = !allowed;
      if (!allowed) this.bodies.get(name)?.setAttribute('hidden', '');
    }

    this.paintSideActive();
    this.paintSides();
    // The tab strip belongs to the terminal. A phone has a home screen.
    this.tabs.hidden = !enforcement;
    this.home.hidden = enforcement;

    if (enforcement) {
      this.phoneBar.hidden = true;
      this.hideAppActions();
      // CALIBRATION.md finding 1: alerts outnumber searches nearly twelve to
      // one, so triage is where an officer starts.
      this.select('alerts');
    } else {
      // And a phone opens on its apps rather than inside one. Which app it
      // would have picked is a guess about what somebody unlocked their phone
      // for, and there is no need to guess.
      this.active = 'work';
      this.goHome();
    }
  }

  get enabled(): boolean {
    return this.live;
  }

  enable(enforcement: boolean): void {
    this.live = true;
    this.setRole(enforcement);
    this.paintOpener();
  }

  /** Whether the terminal is up, and therefore whether the road is being watched. */
  get open(): boolean {
    return this.showing;
  }

  /**
   * Put the terminal up, or take it down.
   *
   * Deliberate, both ways. There is no state in which it is simply always
   * there, because that is the state in which querying is free.
   */
  toggle(): void {
    if (!this.live) return;
    this.showing = !this.showing;
    this.root.hidden = !this.showing;
    this.paintOpener();
  }

  close(): void {
    if (!this.showing) return;
    this.showing = false;
    this.root.hidden = true;
    this.paintOpener();
  }

  private paintOpener(): void {
    this.opener.hidden = !this.live;
    this.opener.textContent = this.showing ? 'close' : this.openerLabel();
    this.opener.classList.toggle('on', this.showing);
    // The dashboard is full width, so the close control belongs at the panel's
    // own top-right rather than the spot that clears the narrow 560px terminal.
    this.opener.classList.toggle('dashboard', this.showing && this.adminView);
  }

  /** What the button says when it is shut, which depends on whose device it is. */
  private openerLabel(): string {
    return this.enforcing ? 'terminal' : 'phone';
  }

  /**
   * What each app is called on the home screen and in its own bar.
   *
   * Capitalised, because these are the names of things rather than the labels
   * on a readout. The console's own stat rows stay lower case: those are
   * measurements, and a measurement is not a proper noun.
   */
  private static readonly APP_NAMES: Record<TabName, string> = {
    work: 'Work',
    garage: 'Garage',
    store: 'Store',
    flocked: 'Flocked',
    alerts: 'Alerts',
    query: 'Search',
    dispatch: 'Dispatch',
    standing: 'Standing',
    audit: 'Log',
    cameras: 'Cameras',
    scanner: 'Scanner',
    settings: 'Settings',
    admin: 'Admin',
  };

  /** Controls belonging to one app, parked in the phone's bar where a phone puts them. */
  private static readonly APP_ACTIONS: Partial<Record<TabName, string>> = {
    flocked: 'flocked-refresh',
  };

  select(name: TabName): void {
    this.active = name;
    this.unread.delete(name);
    // Opening an app asks it for fresh contents. The work board turns over on
    // its own timer, so whatever arrived last is already out of date by the
    // time somebody goes looking at it.
    this.onOpen?.(name);
    for (const [key, body] of this.bodies) body.hidden = key !== name;
    for (const button of this.tabs.querySelectorAll<HTMLElement>('.tab')) {
      const isActive = button.dataset.tab === name;
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-selected', String(isActive));
    }
    if (!this.enforcing) this.openPhoneApp(name);
    this.paintBadges();
  }

  /**
   * Back to the apps.
   *
   * Only a phone has this. Coming out of an app is a thing you do, and it is
   * the difference between a device you carry and a console you sit at.
   */
  /** Called with the app that was just opened. See `select`. */
  whenOpened(listener: (name: TabName) => void): void {
    this.onOpen = listener;
  }

  goHome(): void {
    if (this.enforcing) return;
    this.openApp = null;
    for (const body of this.bodies.values()) body.hidden = true;
    this.home.hidden = false;
    this.phoneBar.hidden = true;
    this.hideAppActions();
    this.paintBadges();
  }

  /** Open one app over the home screen, with its name and its controls above it. */
  /**
   * Show the Store on the home screen. Its tile ships hidden and appears only
   * when the server says it can sell something, so a build with no Stripe key
   * never offers a store that cannot take a dollar.
   */
  revealStore(): void {
    const tile = this.home.querySelector<HTMLElement>('.app-tile[data-tab="store"]');
    if (tile) tile.hidden = false;
  }

  private openPhoneApp(name: TabName): void {
    this.openApp = name;
    this.home.hidden = true;
    this.phoneBar.hidden = false;
    this.phoneTitle.textContent = ConsolePanel.APP_NAMES[name];
    this.hideAppActions();
    const action = ConsolePanel.APP_ACTIONS[name];
    if (action) {
      const button = document.getElementById(action);
      // The app decides whether its own control means anything yet -- there is
      // nothing to refresh on a screen that has never been asked a question --
      // so the bar offers the slot and does not fill it.
      if (button && button.dataset.ready === '1') button.hidden = false;
    }
  }

  private hideAppActions(): void {
    for (const id of Object.values(ConsolePanel.APP_ACTIONS)) {
      const button = document.getElementById(id);
      if (button) button.hidden = true;
    }
  }

  /**
   * Something arrived in a tab the player is not looking at.
   *
   * A dot, not a count and not a color change. The job is to say "there is
   * something here" without deciding for anybody how urgent it is -- the same
   * restraint the audit log takes with an unexplained search.
   */
  notify(name: TabName): void {
    if (this.active === name) return;
    this.unread.add(name);
    this.paintBadges();
  }

  private paintBadges(): void {
    for (const button of this.tabs.querySelectorAll<HTMLElement>('.tab')) {
      const name = button.dataset.tab as TabName;
      button.classList.toggle('unread', this.unread.has(name));
    }
    // On the phone the dot goes on the app's own tile, which is where somebody
    // looking at a home screen would expect to find it.
    for (const tile of this.home.querySelectorAll<HTMLElement>('.app-tile')) {
      const name = tile.dataset.tab as TabName;
      const waiting = this.unread.has(name);
      tile.classList.toggle('unread', waiting);
      const badge = tile.querySelector<HTMLElement>('.app-badge');
      if (badge) badge.hidden = !waiting;
    }
  }
}

function must(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`missing element #${id}`);
  return element;
}
